import { FC, MouseEvent, ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Box, Button, rem } from '@bonusly/design-system';
import { useBuildMonarchURL, useBuildRecognizeURL } from '@bonusly/url';

import { useIsRecognize } from '../../../../hooks/use-is-recognize';

interface Props {
	badgeCount?: number;
	label: string | ReactElement;
	matchActive?: RegExp;
	showAlertDot?: boolean;
	url: string;
	isRecognizeRoute?: boolean; // true if this route should always go to Recognize domain
	external?: boolean; // true if this should open in a new tab (external site)
	onClick?: () => void;
}

export const SmartNavLink: FC<Props> = ({
	badgeCount = 0,
	label,
	matchActive,
	showAlertDot = false,
	url,
	isRecognizeRoute = false,
	external = false,
	onClick,
}) => {
	const isRecognize = useIsRecognize();
	const monarchURL = useBuildMonarchURL();
	const recognizeURL = useBuildRecognizeURL();

	// Use state to track pathname so component re-renders on URL change
	const [currentPathname, setCurrentPathname] = useState(window.location.pathname);

	// Listen for URL changes
	useEffect(() => {
		const handleLocationChange = () => {
			setCurrentPathname(window.location.pathname);
		};

		// Check periodically for URL changes
		const intervalId = setInterval(handleLocationChange, 500);

		return () => {
			clearInterval(intervalId);
		};
	}, []);

	// Determine if this is an explicitly external URL (starts with http)
	const isExternalUrl = url.match(/^http/);

	// Build the appropriate URL based on current domain and route type
	const buildUrl = () => {
		// If it's already a full URL, use it as-is
		if (isExternalUrl) {
			return url;
		}

		// If we're on Recognize and this is a Monarch route (most admin routes)
		if (isRecognize && !isRecognizeRoute) {
			return monarchURL(url);
		}

		// If we're on Monarch and this is a Recognize route
		if (!isRecognize && isRecognizeRoute) {
			return recognizeURL(url);
		}

		// Otherwise, use the relative path (same domain navigation)
		return url;
	};

	const finalUrl = buildUrl();
	const isCrossDomain = (isRecognize && !isRecognizeRoute && !isExternalUrl) || (!isRecognize && isRecognizeRoute && !isExternalUrl);

	// Check if the current route is active
	const checkIsActive = () => {
		// For recognize routes, we need to compare just the pathname
		let linkPath = url;

		// If the url contains a full URL (from useBuildRecognizeURL), extract just the path
		if (url.includes('http')) {
			try {
				const urlObj = new URL(url);
				linkPath = urlObj.pathname;
			} catch (e) {
				// If it's not a valid URL, use the original value
				linkPath = url;
			}
		}

		// Check exact match
		if (linkPath === currentPathname) {
			return true;
		}

		// Check matchActive regex pattern
		if (matchActive && matchActive.test(currentPathname)) {
			return true;
		}

		return false;
	};

	const isActive = checkIsActive();

	// Handle click for cross-domain navigation
	const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
		onClick?.();
		if (isCrossDomain || isRecognize) {
			e.preventDefault();
			window.location.href = finalUrl;
		}
	};

	// For external URLs, use an anchor tag with target="_blank"
	const buttonProps = {
		colorScheme: 'secondary' as const,
		variant: 'ghost' as const,
		borderRadius: 'sm',
		h: 'full',
		w: 'full',
		py: 1,
		px: 2,
		fontSize: 50,
		fontWeight: 'medium' as const,
		display: 'flex',
		justifyContent: 'space-between',
		verticalAlign: 'middle',
		whiteSpace: 'wrap' as const,
		_hover: { background: 'secondary.bgAlpha', color: 'secondary.fgActive', textDecoration: 'none' },
		...(isActive && {
			bg: 'secondary.bg',
			color: 'secondary.fgOnSurface',
			'data-testactive': 'true',
			_active: { bg: 'secondary.bgActive', color: 'secondary.fgOnSurface' },
			_hover: { bg: 'secondary.bgHover', color: 'secondary.fgOnSurface', textDecoration: 'none' },
		}),
	};

	// Only use anchor tag with target="_blank" for explicitly external links
	// Everything else (including cross-domain internal) uses Link + handleClick
	// handleClick will use window.location.href for proper full-page navigation
	const shouldUseAnchor = external === true;

	return (
		<Button
			as={shouldUseAnchor ? 'a' : Link}
			{...(shouldUseAnchor ? { href: finalUrl, target: '_blank', rel: 'noopener noreferrer', onClick } : { to: finalUrl, onClick: handleClick })}
			{...buttonProps}
		>
			{label}
			{badgeCount > 0 && (
				<Box
					data-testid="badge-count"
					bgColor="danger.bg"
					borderRadius="full"
					color="text.onColor"
					display="inline-block"
					h={rem(18)}
					w={rem(18)}
					textAlign="center"
					fontSize={1}
					fontWeight="semibold"
				>
					{badgeCount}
				</Box>
			)}
			{showAlertDot && badgeCount === 0 && <Box data-testid="alert-dot" bgColor="danger.bg" borderRadius="full" h={2} w={2} flexShrink={0} />}
		</Button>
	);
};
